import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { CardContent } from "@/components/ui/card"
import { AnimatedSection } from "./AnimatedSection"
import { FloatingCard } from "./FloatingCard"
import { Hero } from "./Hero"
import { ArrowRight } from "lucide-react"
import { Link } from "wouter"
import ReactGA from "react-ga4"

const isProduction = import.meta.env.PROD ||
  window.location.hostname === "socialmedia-a-team.com" ||
  window.location.hostname === "www.socialmedia-a-team.com";

const trackCtaClick = (buttonLocation: string) => {
  if (isProduction) {
    ReactGA.event({
      category: "engagement",
      action: "cta_click",
      label: buttonLocation
    });
  }
};

export function Landing() {
  // SIGNUP_MODE drives which entry CTAs show (server is the source of truth via /api/config).
  const [signupMode, setSignupMode] = useState<"waitlist" | "direct" | "both">("both")
  useEffect(() => {
    fetch("/api/config")
      .then((r) => r.json())
      .then((c) => { if (c?.signupMode) setSignupMode(c.signupMode) })
      .catch(() => {})
  }, [])
  // "Als Arbeitgeber bewerben" goes straight to direct registration unless we're waitlist-only.
  const applyHref = signupMode === "waitlist" ? "/vsl" : "/login?tab=register"
  const showWaitlistCta = signupMode === "both"

  const handleGetStarted = () => {
    // Redirect to custom login page
    window.location.href = "/login"
  }

  const handleLogin = () => {
    // Direct login for existing customers
    window.location.href = "/login"
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-card to-background">
      {/* Navigation */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-all duration-300">
        <div className="container flex h-16 items-center justify-between px-6 lg:px-8">
          <AnimatedSection direction="left" className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center transition-transform duration-300 hover:scale-110">
              <span className="text-primary-foreground font-bold text-lg font-playfair">A</span>
            </div>
            <span className="text-xl font-bold text-primary font-playfair">Social Media A-Team</span>
          </AnimatedSection>
          <AnimatedSection direction="right">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Button 
                variant="outline"
                size="sm"
                className="group transition-all duration-300 hover:scale-105 text-xs sm:text-sm px-2 sm:px-4"
                onClick={handleLogin}
                data-testid="button-nav-login"
              >
                <span className="hidden sm:inline">Hi, Boss!</span>
                <span className="sm:hidden">Login</span>
              </Button>
              <Button 
                asChild
                size="sm"
                className="group transition-all duration-300 hover:scale-105 text-xs sm:text-sm px-2 sm:px-4"
                data-testid="button-nav-start"
                onClick={() => trackCtaClick("nav")}
              >
                <Link href={applyHref}>
                  <span className="hidden sm:inline">Als Arbeitgeber bewerben</span>
                  <span className="sm:hidden">Bewerben</span>
                  <ArrowRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </header>
      {/* Hero Section */}
      <Hero />
      {/* CTA Section */}
      <section className="py-20">
        <div className="container px-6 lg:px-8">
          <AnimatedSection className="text-center">
            <FloatingCard className="max-w-3xl mx-auto">
              <CardContent className="pt-8 pb-8 space-y-6">
                <h2 className="text-3xl lg:text-4xl font-bold font-playfair">Socialmedia-A-Team.com</h2>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={handleLogin}
                    data-testid="button-cta-login"
                    className="group transition-all duration-300 hover:scale-105"
                  >
                    👋 Hi, Boss!
                  </Button>
                  <Button 
                    size="lg" 
                    asChild
                    data-testid="button-cta-start"
                    className="group transition-all duration-300 hover:scale-105"
                    onClick={() => trackCtaClick("hero")}
                  >
                    <Link href={applyHref}>
                      Als Arbeitgeber bewerben
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                  {showWaitlistCta && (
                    <Button
                      size="lg"
                      variant="ghost"
                      asChild
                      data-testid="button-cta-waitlist"
                      className="group transition-all duration-300 hover:scale-105"
                    >
                      <Link href="/vsl">Auf die Warteliste</Link>
                    </Button>
                  )}
                </div>
              </CardContent>
            </FloatingCard>
          </AnimatedSection>
        </div>
      </section>
      {/* Footer */}
      <footer className="border-t border-border/40 py-6 sm:py-8">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 items-center">
            <div className="flex items-center space-x-2">
              <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-xs sm:text-sm font-playfair">A</span>
              </div>
              <span className="font-semibold text-primary font-playfair text-sm sm:text-base">Social Media A-Team</span>
            </div>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
              <span>© 2025 SumoBrands</span>
              <span className="hidden sm:inline">•</span>
              <Link href="/impressum" className="hover:text-primary hover:underline" data-testid="link-footer-impressum">
                Impressum
              </Link>
              <Link href="/datenschutz" className="hover:text-primary hover:underline" data-testid="link-footer-datenschutz">
                Datenschutz
              </Link>
              <Link href="/agb" className="hover:text-primary hover:underline" data-testid="link-footer-agb">
                AGB
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}